import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	PunchupColumnHeader,
	PunchupDataRow,
	PunchupDateTimeCell,
	PunchupEntityCell,
	PunchupHeaderRow,
	PunchupNameCell,
	PunchupTableContainer,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupLoadMore } from 'features/common/components/Punchup/composites/tables/PunchupLoadMore';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { ScanHistoryTicket } from 'hooks/useGetScanHistory';
import { formatDate } from 'features/common/utils/dateUtils';
import { useResizableColumns } from 'features/common/hooks/useResizableColumns';
import { SortField, SortOrder } from '../types';
import { SCANS_COLUMN_KEYS, SCANS_SEAT_KEY } from './constants';

interface PunchupScansTableProps {
	scans: ScanHistoryTicket[];
	/** Seated events carry a seat per ticket — render the Seat column only then. */
	hasSeats: boolean;
	/** A search is active — drives the "no match" vs "no scans yet" empty copy. */
	isSearching: boolean;
	isLoading: boolean;
	sortBy: SortField;
	order: SortOrder;
	onSort: (field: SortField) => void;
	/** True while an already-loaded table re-fetches — dims the table with a spinner overlay. */
	isRefetching?: boolean;
}

export const PunchupScansTable: FC<PunchupScansTableProps> = ({
	scans,
	hasSeats,
	isSearching,
	isLoading,
	sortBy,
	order,
	onSort,
	isRefetching,
}) => {
	const { t } = useTranslation();
	// Resolve from the shared source so an unset type shows its stable palette colour (not grey),
	// consistent with every other surface — `scan.ticketTypeColor` only carries an explicitly-set colour.
	const { resolveByName } = useTicketTypeColors();

	const emptyText = isSearching
		? t('scans-table-no-results', 'No scanned tickets match your search.')
		: t('scans-table-empty', 'No scans yet — tickets appear here as guests are scanned in at the door.');

	const keys = useMemo(() => (hasSeats ? [...SCANS_COLUMN_KEYS, SCANS_SEAT_KEY] : [...SCANS_COLUMN_KEYS]), [hasSeats]);
	const { gridColumns, setColumnWidth } = useResizableColumns(keys);

	const sortDir = (field: SortField) => {
		if (sortBy !== field) return null;
		return order === 'asc' ? ('asc' as const) : ('desc' as const);
	};

	return (
		<>
			<PunchupTableContainer columns={gridColumns} isRefetching={isRefetching}>
				<thead>
					<PunchupHeaderRow>
						<PunchupColumnHeader
							sortable
							sortDirection={sortDir('scannedAt')}
							onSort={() => onSort('scannedAt')}
							onResize={(w) => setColumnWidth('scannedAt', w)}
						>
							{t('scans-table-scan-time', 'Scan Time')}
						</PunchupColumnHeader>
						<PunchupColumnHeader
							sortable
							sortDirection={sortDir('fullName')}
							onSort={() => onSort('fullName')}
							onResize={(w) => setColumnWidth('fullName', w)}
						>
							{t('scans-table-attendee', 'Attendee')}
						</PunchupColumnHeader>
						<PunchupColumnHeader onResize={(w) => setColumnWidth('email', w)}>
							{t('scans-table-email', 'Email')}
						</PunchupColumnHeader>
						<PunchupColumnHeader
							sortable
							sortDirection={sortDir('ticketType')}
							onSort={() => onSort('ticketType')}
							onResize={(w) => setColumnWidth('ticketType', w)}
						>
							{t('scans-table-ticket-type', 'Ticket Type')}
						</PunchupColumnHeader>
						{hasSeats && (
							<PunchupColumnHeader onResize={(w) => setColumnWidth('seat', w)}>
								{t('scans-table-seat', 'Seat')}
							</PunchupColumnHeader>
						)}
					</PunchupHeaderRow>
				</thead>
				<tbody>
					{isLoading ? (
						<PunchupTableLoadingRow />
					) : scans.length === 0 ? (
						<PunchupTableEmptyRow text={emptyText} />
					) : (
						scans.map((scan) => (
							<PunchupDataRow key={scan.ticket.id}>
								<PunchupDateTimeCell>
									{scan.ticket.scannedAt ? formatDate(scan.ticket.scannedAt) : '—'}
								</PunchupDateTimeCell>
								<PunchupEntityCell>{scan.fullName}</PunchupEntityCell>
								<PunchupNameCell>{scan.email}</PunchupNameCell>
								<PunchupNameCell>
									<PunchupTicketTypeTag name={scan.ticketTypeName} color={resolveByName(scan.ticketTypeName)} />
								</PunchupNameCell>
								{hasSeats && <PunchupNameCell>{scan.ticket.seatId || '—'}</PunchupNameCell>}
							</PunchupDataRow>
						))
					)}
				</tbody>
			</PunchupTableContainer>

			<PunchupLoadMore isDisabled handleClick={() => {}} />
		</>
	);
};
